import { Fragment } from "react"
import { cn } from "../../lib/utils"

/** Escapes a string so it can be dropped into a `RegExp` as a literal. */
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

export interface HighlightedTextProps {
  text: string
  /** Substring to mark, matched case-insensitively against every occurrence. */
  query?: string
  /** Class applied to each marked segment. */
  className?: string
}

/**
 * Renders `text` with every case-insensitive occurrence of `query` set in
 * bold. The weight is the whole mark — no fill behind it, since a tinted
 * block in a list of titles reads as a state the row is in rather than as the
 * letters that matched. Falls back to the plain text when `query` is empty,
 * whitespace only, or has no match.
 */
export const HighlightedText = ({ text, query, className }: HighlightedTextProps) => {
  const trimmedQuery = query?.trim()
  if (!trimmedQuery) return <Fragment>{text}</Fragment>

  const pattern = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "gi")
  const parts = text.split(pattern)
  if (parts.length === 1) return <Fragment>{text}</Fragment>

  return (
    <Fragment>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <mark
            key={index}
            className={cn("bg-transparent font-bold text-inherit", className)}
          >
            {part}
          </mark>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </Fragment>
  )
}
